import React, { useState, useRef, useEffect } from 'react';
import ScrollArea from './scrollArea';

interface Option {
  value: string;
  label: string;
}

interface BusinessAreasDropdownProps {
  name: string;
  value: string[];
  onChange: (values: string[]) => void;
  options: Option[];
  placeholder?: string;
  className?: string;
  allowOther?: boolean;
}

const BusinessAreasDropdown: React.FC<BusinessAreasDropdownProps> = ({
  name,
  value = [],
  onChange,
  options,
  placeholder = 'Select options',
  className = '',
  allowOther = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [otherValue, setOtherValue] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const otherInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleOptionClick = (optionValue: string) => {
    const newValues = value.includes(optionValue)
      ? value.filter(v => v !== optionValue)
      : [optionValue, ...value.filter(v => v !== optionValue)];
    onChange(newValues);
  };

  const handleRemoveTag = (tagValue: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const newValues = value.filter(v => v !== tagValue);
    onChange(newValues);
  };

  const handleOtherInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && otherValue.trim()) {
      e.preventDefault();
      onChange([otherValue.trim(), ...value]);
      setOtherValue('');
      setIsOpen(false);
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <div
        onClick={() => setIsOpen(!isOpen)}
        className={`relative h-[36px] border border-[#EEEFF1] rounded-lg bg-white
          cursor-pointer ${className}`}
      >
        <div className="absolute inset-0 flex items-center px-2">
          <div className="flex-1 overflow-x-auto whitespace-nowrap scrollbar-hide [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            <div className="inline-flex items-center gap-1.5 h-[24px]">
              {value.length > 0 ? (
                value.map((val) => {
                  const option = options.find(opt => opt.value === val);
                  return (
                    <span
                      key={val}
                      className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#E8FFF0] text-[#12692B] text-xs rounded-lg border border-[#81F4A1]"
                    >
                      {option?.label || val}
                      <button
                        onClick={(e) => handleRemoveTag(val, e)}
                        className="text-[#12692B] hover:text-[#0A4F1D]"
                      >
                        ×
                      </button>
                    </span>
                  );
                })
              ) : (
                <span className="text-[#9FA1A7] text-sm">{placeholder}</span>
              )}
            </div>
          </div>
          <div className="flex-shrink-0 ml-2 bg-white pl-1">
            <svg
              className={`w-3.5 h-3.5 transition-transform ${isOpen ? 'transform rotate-180' : ''} text-[#7A7A7A]`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
      </div>

      {isOpen && (
        <div className="absolute z-10 w-full mt-1 bg-[#FFFFFF] border border-[#EEEFF1] rounded-lg shadow-lg">
          <ScrollArea className="max-h-[180px]">
            <div className="p-2 space-y-1">
              {options.map((option) => (
                <div
                  key={option.value}
                  onClick={() => handleOptionClick(option.value)}
                  className={`px-3 py-2 cursor-pointer rounded-lg text-xs ${
                    value.includes(option.value)
                      ? 'bg-[#EEEFF1] text-[#222222]'
                      : 'hover:bg-[#FAFAFA] text-[#222222]'
                  }`}
                >
                  {option.label}
                </div>
              ))}
              {allowOther && (
                <div className="px-3 py-2">
                  <input
                    ref={otherInputRef}
                    type="text"
                    value={otherValue}
                    onChange={(e) => setOtherValue(e.target.value)}
                    onKeyDown={handleOtherInputKeyDown}
                    placeholder="Enter custom option"
                    className="w-full px-2 py-1 text-xs border border-[#EEEFF1] rounded-lg
                      focus:outline-none focus:ring-1 focus:ring-[#29DC5B] focus:border-transparent"
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
              )}
            </div>
          </ScrollArea>
        </div>
      )}
    </div>
  );
};

export default BusinessAreasDropdown; 